/**
 * V2.0.34 — Project-Unlock paywall.
 *
 * Replaces the legacy "$19 Studio Tier" paywall card with a tier-aware
 * dialog that surfaces the new per-project pricing model:
 *
 *   - Tier 1 / Consumer Visualizer ($29)      → 2D PDF + 3 AI renders
 *   - Tier 2A / Smart Start (≤1,500 sqft)     → $99
 *   - Tier 2B / Residential Standard (≤3,000) → $179
 *   - Tier 2C / Custom Premier (>3,000)       → $299 + $100/1k sqft above 5,000
 *
 * The component fetches a live unlock quote on mount (computed from
 * the current blueprint, never a stale snapshot) and surfaces a CTA
 * with the exact dollar amount the user owes. If the user already has
 * a sufficient unlock, the paywall closes automatically and triggers
 * the retry callback so the underlying download fires immediately.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Lock, Loader2, Sparkles, ShieldCheck, Wand2, Gift } from "lucide-react";
import { toast } from "sonner";
import { getUnlockQuote, redeemReferralTier1, startUnlockCheckout } from "../lib/api";

const ARCHITECT_TIER_IDS = ["smart_start", "residential_standard", "custom_premier"];

function formatPrice(amount) {
  if (amount == null) return "—";
  const n = Number(amount);
  return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
}

function formatSqft(sqft) {
  const n = Math.round(Number(sqft) || 0);
  return n.toLocaleString();
}

export default function ProjectUnlockPaywall({
  designId,
  // "pdf" → defaults to the auto-resolved architect tier so the user
  //         sees an IFC-compatible offer (the IFC is part of every
  //         architect-tier deliverable).
  // "ifc" / "package" → architect tier required, same flow.
  // "visualizer" → forces the Tier 1 $29 visualizer offer.
  intent = "pdf",
  onClose,
  onUnlocked,
}) {
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState("");

  // Pull the initial quote — backend auto-picks the smallest architect
  // tier for the live sqft when no tier_id is passed.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const initialTier = intent === "visualizer" ? "visualizer" : null;
    getUnlockQuote(designId, initialTier)
      .then((r) => {
        if (cancelled) return;
        const q = r.data || {};
        setQuote(q);
        setSelectedTier(q?.requested_tier?.id || (intent === "visualizer" ? "visualizer" : "smart_start"));
        // Already paid ⇒ instant unlock retry.
        if (q?.already_paid) {
          toast.success("Re-downloading — this design is already unlocked.");
          onUnlocked?.();
        }
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load pricing. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [designId, intent, onUnlocked]);

  // Re-quote when the user picks a different tier in the dialog.
  useEffect(() => {
    if (!selectedTier || !quote || selectedTier === quote.requested_tier?.id) return;
    let cancelled = false;
    getUnlockQuote(designId, selectedTier).then((r) => {
      if (!cancelled) setQuote(r.data || quote);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedTier, designId, quote]);

  const tierOptions = useMemo(() => {
    if (!quote?.all_tiers) return [];
    if (intent === "visualizer") {
      return quote.all_tiers.filter((t) => t.id === "visualizer");
    }
    // For PDF/IFC/package the architect ladder is shown so the user can
    // self-upgrade (e.g. they want IFC even though current sqft is tiny).
    return quote.all_tiers.filter((t) => ARCHITECT_TIER_IDS.includes(t.id));
  }, [quote, intent]);

  const handleUnlock = async () => {
    if (!selectedTier) return;
    setCheckingOut(true);
    try {
      const r = await startUnlockCheckout(designId, selectedTier);
      const { url, already_paid } = r.data || {};
      if (already_paid) {
        toast.success("Already unlocked — re-downloading now.");
        onUnlocked?.();
        return;
      }
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setCheckingOut(false);
    }
  };

  // V2.0.41 — Redeem a free Tier 1 unlock earned via the architect-
  // referral flywheel. One click, no Stripe round-trip. Only shown when
  // the visitor's intent is Tier 1 ("pdf"/"visualizer") AND they have
  // at least one credit AND the design isn't already unlocked.
  const referralCreditCount = quote?.referral_tier1_credits || 0;
  const canRedeemReferral =
    intent !== "ifc" &&
    referralCreditCount > 0 &&
    !quote?.already_paid;

  const handleRedeemReferral = async () => {
    if (!canRedeemReferral) return;
    setRedeeming(true);
    try {
      const r = await redeemReferralTier1(designId);
      if (r.data?.redeemed) {
        toast.success("Free Tier 1 unlock applied!", {
          description: "Your design is now unlocked — generating files now.",
        });
        onUnlocked?.();
      } else {
        toast.error("Couldn't apply the credit. Please try again.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("no referral")) {
        toast.error("No referral credits available.");
      } else {
        toast.error("Couldn't apply the credit. Please try again.");
      }
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="project-unlock-paywall"
        className="absolute inset-0 z-30 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-6"
      >
        <div className="flex items-center gap-3 text-brass-300 font-mono text-xs uppercase tracking-widest">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculating price…
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div
        data-testid="project-unlock-paywall"
        className="absolute inset-0 z-30 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-6"
        onClick={onClose}
      >
        <div className="max-w-md bg-zinc-950 border-2 border-red-700 p-6 text-center" onClick={(e) => e.stopPropagation()}>
          <p className="text-red-300 mb-3">{error || "Couldn't load pricing."}</p>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-widest border border-zinc-700 px-3 py-1.5 text-zinc-300 hover:bg-zinc-800/60"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const tier = quote.requested_tier;
  const price = quote.price_usd;
  const grossSqft = quote.gross_sqft;
  const isVisualizer = intent === "visualizer";

  return (
    <div
      data-testid="project-unlock-paywall"
      className="absolute inset-0 z-30 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-zinc-950 border-2 border-brass-500 shadow-[0_0_60px_-20px_rgba(196,169,107,0.45)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-800 flex items-start gap-3">
          <div className="w-10 h-10 grid place-items-center border border-brass-500/60 bg-brass-950/40">
            <Lock className="w-4 h-4 text-brass-300" />
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
              // {isVisualizer ? "Tier 1 · Consumer Visualizer" : "Architect-Ready Package"}
            </div>
            <div className="font-display text-zinc-50 text-lg mt-0.5">
              {isVisualizer
                ? "Unlock 2D PDF + 3 AI photo renders"
                : tier?.bundled_ai_renders
                ? `Unlock IFC + PDF + ${tier.bundled_ai_renders} AI Renders`
                : "Unlock IFC + PDF + Audit Log"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1.5">
              Current design area: <span className="text-zinc-300">{formatSqft(grossSqft)} sqft</span>
            </div>
          </div>
        </div>

        {/* Tier selection (architect tiers only when intent != visualizer) */}
        {!isVisualizer && tierOptions.length > 1 && (
          <div className="px-6 pt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {tierOptions.map((t) => {
              const active = t.id === selectedTier;
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`unlock-tier-${t.id}`}
                  onClick={() => setSelectedTier(t.id)}
                  className={
                    "text-left border p-3 transition-colors " +
                    (active
                      ? "border-brass-500 bg-brass-950/30"
                      : "border-zinc-800 hover:border-zinc-600 bg-zinc-950")
                  }
                >
                  <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 mb-1">
                    {t.id === "smart_start"
                      ? "Tier 2A · Smart Start"
                      : t.id === "residential_standard"
                      ? "Tier 2B · Residential"
                      : "Tier 2C · Premier"}
                  </div>
                  <div className="font-display text-zinc-100 text-base">
                    {t.max_sqft ? `≤ ${t.max_sqft.toLocaleString()} sqft` : ">5,000 sqft"}
                  </div>
                  <div className="font-mono text-xs text-zinc-300 mt-2">
                    From {formatPrice(t.base_price_usd)}
                  </div>
                  {t.bundled_ai_renders > 0 && (
                    <div
                      data-testid={`unlock-tier-${t.id}-renders`}
                      className="mt-1.5 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-brass-300"
                    >
                      <Wand2 className="w-2.5 h-2.5" />
                      Includes {t.bundled_ai_renders} AI renders
                      {t.bundled_ai_renders_value_usd > 0 && (
                        <span className="text-brass-400/90">
                          &nbsp;(worth ${Math.round(t.bundled_ai_renders_value_usd)})
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Live quote summary */}
        <div className="px-6 py-5">
          <div className="border border-brass-700/60 bg-brass-950/20 p-4 flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-brass-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-200 leading-relaxed" data-testid="unlock-quote-message">
                {quote.message}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-widest">
                <div>
                  <div className="text-zinc-500">Tier</div>
                  <div className="text-zinc-100 mt-0.5">{tier?.label}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Sqft</div>
                  <div className="text-zinc-100 mt-0.5">{formatSqft(grossSqft)}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Price</div>
                  <div className="text-brass-200 mt-0.5 text-sm" data-testid="unlock-quote-price">{formatPrice(price)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Value bullets */}
          <ul className="mt-4 space-y-2 text-xs text-zinc-300">
            {isVisualizer ? (
              <>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
                  <span>Clean 2D architectural floor-plan PDF — print at home for permits or contractor walkthroughs.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Wand2 className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
                  <span>3 photo-realistic AI exterior renders (Nano Banana) — perfect for sharing with family.</span>
                </li>
              </>
            ) : (
              <>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
                  <span>Foundational <strong>IFC 4 BIM file</strong> — opens directly in Revit, ArchiCAD, Vectorworks, BlenderBIM.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
                  <span>High-fidelity Architectural PDF with dimensions, door swings, code-compliance callouts.</span>
                </li>
                {tier?.bundled_ai_renders > 0 && (
                  <li
                    className="flex items-start gap-2"
                    data-testid="unlock-included-renders"
                  >
                    <Wand2 className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>{tier.bundled_ai_renders} photo-realistic AI renders</strong> (Nano Banana)
                      {tier.bundled_ai_renders_value_usd > 0 && (
                        <> — a <strong>${Math.round(tier.bundled_ai_renders_value_usd)} value</strong> bundled free</>
                      )}
                      . No extra credit purchase needed.
                    </span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
                  <span>Signed acceptance log + energy score + code-compliance audit — ready for your architect / GC.</span>
                </li>
                <li className="flex items-start gap-2 text-zinc-500 italic">
                  <span>Unlimited re-downloads while the design stays within your tier ceiling.</span>
                </li>
              </>
            )}
          </ul>
        </div>

        {/* V2.0.41 — Referral redemption chip. Brass-on-brass pill shown
            above the paid CTA when the user has at least one credit
            earned via the architect-referral flywheel. */}
        {canRedeemReferral && (
          <div className="px-6 pb-2">
            <button
              type="button"
              data-testid="unlock-redeem-referral-btn"
              onClick={handleRedeemReferral}
              disabled={redeeming}
              className="w-full inline-flex items-center justify-between gap-3 px-4 py-3 border border-brass-500 bg-gradient-to-r from-brass-950/40 to-brass-900/30 hover:from-brass-900/40 hover:to-brass-800/30 text-brass-100 font-mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="inline-flex items-center gap-2">
                {redeeming ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Gift className="w-3.5 h-3.5 text-brass-300" />
                )}
                {redeeming
                  ? "Applying your free unlock…"
                  : "Use your free architect-referral unlock"}
              </span>
              <span className="text-brass-300 normal-case tracking-normal text-[10px]">
                {referralCreditCount} available
              </span>
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between gap-3 bg-zinc-950">
          <button
            type="button"
            onClick={onClose}
            data-testid="unlock-cancel-btn"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
          >
            Maybe later
          </button>
          <button
            type="button"
            data-testid="unlock-checkout-btn"
            onClick={handleUnlock}
            disabled={checkingOut}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brass-500 hover:bg-brass-400 text-zinc-950 font-mono text-xs uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkingOut ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
            {checkingOut ? "Redirecting…" : `Pay ${formatPrice(price)} & Unlock`}
          </button>
        </div>
      </div>
    </div>
  );
}
